import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Package as PkgIcon, Plus, Edit2, Trash2, Loader2, X, Eye, EyeOff,
  Archive, RotateCcw, ArrowUp, ArrowDown, Star, Globe, Lock,
} from "lucide-react";

const EMPTY = {
  name_ar: "", name_en: "", price: "", price_en: "",
  features_ar: [], features_en: [], featured: false, order: 0,
  is_active: true, is_public: false, status: "active",
  regulatory_frameworks: "",
};

export default function AdminPackages() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => api.get("/admin/packages").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  const visible = useMemo(() => rows.filter((r) => showArchived ? true : !r.is_archived), [rows, showArchived]);

  const openEdit = (p) => setEditing({
    ...EMPTY, ...p,
    features_ar: Array.isArray(p.features_ar) ? p.features_ar : [],
    features_en: Array.isArray(p.features_en) ? p.features_en : [],
  });

  const save = async () => {
    if (!editing.name_en) { toast.error("Name (EN) required"); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/packages/${editing.id}`, editing);
      else await api.post("/admin/packages", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const togglePublic = async (p) => { try { await api.put(`/admin/packages/${p.id}/public`, { is_public: !p.is_public }); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const archive = async (p) => { if (window.confirm(`Archive "${p.name_en}"?`)) { await api.post(`/admin/packages/${p.id}/archive`); load(); } };
  const restore = async (p) => { await api.post(`/admin/packages/${p.id}/restore`); load(); };
  const remove = async (p) => {
    if (!window.confirm("Delete permanently?")) return;
    try {
      await api.delete(`/admin/packages/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const move = async (p, dir) => {
    const list = [...visible].sort((a, b) => a.order - b.order);
    const idx = list.findIndex((x) => x.id === p.id);
    const t = idx + dir;
    if (t < 0 || t >= list.length) return;
    [list[idx], list[t]] = [list[t], list[idx]];
    await api.put("/admin/packages/reorder", { ordered_ids: list.map((x) => x.id) });
    load();
  };

  return (
    <div data-testid="admin-packages">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <span className="eyebrow">Offering</span>
          <h1 className="mt-2 text-3xl text-navy flex items-center gap-3"><PkgIcon size={26} className="text-gold" /> Packages</h1>
          <p className="text-xs text-navy/60 mt-1">Public visibility is off by default — flip the globe icon when a package is ready.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-navy/70 inline-flex items-center gap-2">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="pkg-show-archived" /> Show archived
          </label>
          <button onClick={() => setEditing({ ...EMPTY, order: rows.length })} className="btn-gold" data-testid="pkg-add"><Plus size={14} /> New</button>
        </div>
      </div>
      <span className="divider-gold" />

      <div className="card-elev divide-y divide-navy/10">
        {visible.length === 0 ? (
          <div className="p-8 text-center text-navy/50">No packages.</div>
        ) : visible.sort((a, b) => a.order - b.order).map((p) => (
          <div key={p.id} className="p-5 flex items-center gap-4" data-testid={`pkg-row-${p.id}`}>
            <div className="flex flex-col gap-0.5">
              <button onClick={() => move(p, -1)} className="text-navy/50 hover:text-gold" data-testid={`pkg-up-${p.id}`}><ArrowUp size={12} /></button>
              <button onClick={() => move(p, +1)} className="text-navy/50 hover:text-gold" data-testid={`pkg-down-${p.id}`}><ArrowDown size={12} /></button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-navy font-medium flex items-center gap-2">
                {p.name_en} <span className="text-xs text-navy/50">· {p.price}</span>
                {p.featured && <Star size={12} className="text-gold fill-gold" />}
              </div>
              <div className="text-xs text-navy/60">{p.name_ar}</div>
            </div>
            <div className="flex items-center gap-1 whitespace-nowrap">
              {p.is_archived ? <Badge className="bg-navy/20 text-navy/70"><Archive size={11} className="inline me-1" />Archived</Badge>
                : p.is_public ? <Badge className="bg-emerald-100 text-emerald-800"><Globe size={11} className="inline me-1" />Public</Badge>
                : <Badge className="bg-amber-100 text-amber-800"><Lock size={11} className="inline me-1" />Private</Badge>}
              {!p.is_archived && <button onClick={() => togglePublic(p)} className="text-navy/70 p-1.5" title={p.is_public ? "Hide from public" : "Make public"} data-testid={`pkg-public-${p.id}`}>
                {p.is_public ? <Lock size={14} /> : <Globe size={14} />}
              </button>}
              <button onClick={() => openEdit(p)} className="text-navy/70 p-1.5" data-testid={`pkg-edit-${p.id}`}><Edit2 size={14} /></button>
              {p.is_archived ? <button onClick={() => restore(p)} className="text-emerald-700 p-1.5" data-testid={`pkg-restore-${p.id}`}><RotateCcw size={14} /></button>
                             : <button onClick={() => archive(p)} className="text-amber-700 p-1.5" data-testid={`pkg-archive-${p.id}`}><Archive size={14} /></button>}
              <button onClick={() => remove(p)} className="text-red-600/80 p-1.5" data-testid={`pkg-del-${p.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-3xl mx-auto rounded-md border border-gold/30 p-8 my-6" onClick={(e) => e.stopPropagation()} data-testid="pkg-editor">
            <div className="flex justify-between">
              <h2 className="text-2xl text-navy">{editing.id ? "Edit package" : "New package"}</h2>
              <button onClick={() => setEditing(null)} className="text-navy/50"><X size={18} /></button>
            </div>
            <span className="divider-gold" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input className="field-box" placeholder="Name (AR)" value={editing.name_ar} onChange={(e) => setEditing({ ...editing, name_ar: e.target.value })} data-testid="pkg-name-ar" />
              <input className="field-box" placeholder="Name (EN)" value={editing.name_en} onChange={(e) => setEditing({ ...editing, name_en: e.target.value })} data-testid="pkg-name-en" />
              <input className="field-box" placeholder="Price (AR)" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
              <input className="field-box" placeholder="Price (EN)" value={editing.price_en || ""} onChange={(e) => setEditing({ ...editing, price_en: e.target.value })} />
              <textarea className="field-box min-h-[110px]" placeholder="Features (AR) — one per line" value={(editing.features_ar || []).join("\n")} onChange={(e) => setEditing({ ...editing, features_ar: e.target.value.split("\n").filter((v) => v.trim()) })} />
              <textarea className="field-box min-h-[110px]" placeholder="Features (EN) — one per line" value={(editing.features_en || []).join("\n")} onChange={(e) => setEditing({ ...editing, features_en: e.target.value.split("\n").filter((v) => v.trim()) })} />
              <input className="field-box" type="number" placeholder="Display order" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) || 0 })} />
              <select className="field-box" value={editing.status || "active"} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="pkg-status">
                <option value="active">Active</option>
                <option value="coming_soon">Coming Soon (قريباً)</option>
                <option value="hidden">Hidden</option>
              </select>
              <label className="flex items-center gap-2 text-navy">
                <input type="checkbox" className="w-5 h-5 accent-gold" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="pkg-featured" /> Featured
              </label>
              <label className="flex items-center gap-2 text-navy">
                <input type="checkbox" className="w-5 h-5 accent-gold" checked={!!editing.is_public} onChange={(e) => setEditing({ ...editing, is_public: e.target.checked })} data-testid="pkg-public-flag" /> Public visibility (show on the public site)
              </label>
              <label className="flex items-center gap-2 text-navy">
                <input type="checkbox" className="w-5 h-5 accent-gold" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} data-testid="pkg-active" /> Active
              </label>
              <div className="md:col-span-2">
                <label className="text-xs text-navy/70 mb-1 block">
                  Regulatory frameworks (CSV — e.g. <code>pdpl,gdpr</code> or <code>🇸🇾 Syrian Data Protection</code>)
                </label>
                <input className="field-box w-full"
                       placeholder="pdpl,gdpr,uae_pdpl,qatar_pdp"
                       value={editing.regulatory_frameworks || ""}
                       onChange={(e) => setEditing({ ...editing, regulatory_frameworks: e.target.value })}
                       data-testid="pkg-reg" />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving} className="btn-gold" data-testid="pkg-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
              </button>
              <button onClick={() => setEditing(null)} className="btn-outline-navy">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
